import { useState, useRef, useMemo, useCallback, DragEvent } from 'react'
import { useUIStore } from '@/stores'
import { useProjects, useFolders, useThreads, useCreateProject, useCreateFolder, useCreateThread, useMoveFolder, useMoveThread, useAvailableModels } from '@/hooks'
import { FolderPlus, MessageSquarePlus, Plus, ChevronRight, ChevronDown, Folder, MessageSquare, LayoutDashboard, Settings, Pin, LogOut, Wallet, Store, Shield, Key, Lock, Code, Bot, FileDown, X, Loader2, Share2, Search, HardDrive } from 'lucide-react'
import type { Folder as FolderType, Thread, Project } from '@/types'
import { useAuthStore } from '@/stores/auth'
import { api } from '@/services/api-client'
import { useT } from '@/i18n'
import { getModelDisplayName } from '@/utils/helpers'
import { useQueryClient } from '@tanstack/react-query'

// Drag data format
const DRAG_TYPE = 'application/synapsios-tree'

interface DragPayload {
  type: 'folder' | 'thread'
  id: string
  projectId: string
  parentFolderId: string | null
}

export function Sidebar() {
  const t = useT()
  const { activeProjectId, activeThreadId, activeView, navigateToDashboard, navigateToProject, navigateToThread, navigateToSettings, navigateToWallet, navigateToMarketplace, navigateToAdmin, navigateToApiKeys, navigateToAppBuilder, navigateToAgent, navigateToSocialAgent, navigateToContentSeo, navigateToFiles } = useUIStore()
  const { data: projects } = useProjects()
  const authUser = useAuthStore(s => s.user)
  const logout = useAuthStore(s => s.logout)
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set())
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const [showNewProject, setShowNewProject] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [newProjectPassword, setNewProjectPassword] = useState('')
  const [unlockedProjects, setUnlockedProjects] = useState<Set<string>>(new Set())
  const [unlockingProject, setUnlockingProject] = useState<string | null>(null)
  const [unlockPassword, setUnlockPassword] = useState('')
  const [unlockError, setUnlockError] = useState('')
  const createProject = useCreateProject()
  const qc = useQueryClient()
  const [showImport, setShowImport] = useState(false)
  const [importFormat, setImportFormat] = useState('openai')
  const [importProjectId, setImportProjectId] = useState('')
  const [importError, setImportError] = useState('')
  const [importing, setImporting] = useState(false)
  const [importFiles, setImportFiles] = useState<File[]>([])
  const [importSuccess, setImportSuccess] = useState(0)
  const importFileRef = useRef<HTMLInputElement>(null)

  const handleImport = async () => {
    if (importFiles.length === 0) { setImportError('Wybierz pliki do importu'); return }
    const targetProjectId = importProjectId || '__standalone_import__'
    setImportError('')
    setImporting(true)
    setImportSuccess(0)
    let successCount = 0
    try {
      for (const file of importFiles) {
        const text = await file.text()
        const data = JSON.parse(text)
        await api.importThread({ project_id: targetProjectId, data, source_format: importFormat })
        successCount++
        setImportSuccess(successCount)
      }
      qc.invalidateQueries({ queryKey: ['threads'] })
      qc.invalidateQueries({ queryKey: ['folders'] })
      qc.invalidateQueries({ queryKey: ['projects'] })
      setShowImport(false)
      setImportFiles([])
      setImportError('')
    } catch (e: any) {
      setImportError(`${successCount > 0 ? `${successCount} importowano. ` : ''}Błąd: ${e.message || 'Import failed'}`)
    } finally {
      setImporting(false)
      if (importFileRef.current) importFileRef.current.value = ''
    }
  }

  const toggleProject = (id: string) => {
    const next = new Set(expandedProjects)
    if (next.has(id)) next.delete(id); else next.add(id)
    setExpandedProjects(next)
  }

  const toggleFolder = (id: string) => {
    const next = new Set(expandedFolders)
    if (next.has(id)) next.delete(id); else next.add(id)
    setExpandedFolders(next)
  }

  const handleCreateProject = () => {
    if (!newProjectName.trim()) return
    createProject.mutate({ name: newProjectName.trim(), password: newProjectPassword || undefined }, {
      onSuccess: () => { setNewProjectName(''); setNewProjectPassword(''); setShowNewProject(false) },
    })
  }

  const handleUnlockProject = async (projectId: string) => {
    try {
      setUnlockError('')
      await api.unlockProject(projectId, unlockPassword)
      setUnlockedProjects(prev => new Set(prev).add(projectId))
      setUnlockingProject(null)
      setUnlockPassword('')
    } catch {
      setUnlockError(t.projWrongPassword)
    }
  }

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <button className="sidebar-logo" onClick={navigateToDashboard}>
          <span className="logo-icon">S</span>
          <span className="logo-text">Synapsios</span>
        </button>
      </div>

      <div className="sidebar-nav">
        <button className={`sidebar-nav-item ${!activeProjectId && !activeThreadId ? 'active' : ''}`} onClick={navigateToDashboard}>
          <LayoutDashboard size={16} />
          <span>{t.navDashboard}</span>
        </button>
      </div>

      <div className="sidebar-section-header">
        <span>{t.navProjects}</span>
        <div className="sidebar-section-actions">
          <button className="sidebar-icon-btn" onClick={() => { setShowImport(true); setImportProjectId(activeProjectId || (projects?.[0]?.id || '')); setImportError(''); setImportFiles([]); setImportSuccess(0) }} title="Import czatów">
            <FileDown size={14} />
          </button>
          <button className="sidebar-icon-btn" onClick={() => setShowNewProject(true)} title={t.sidebarNewProject}>
            <Plus size={14} />
          </button>
        </div>
      </div>

      {showNewProject && (
        <div className="sidebar-inline-form sidebar-thread-form">
          <input
            autoFocus
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreateProject(); if (e.key === 'Escape') { setShowNewProject(false); setNewProjectPassword('') } }}
            placeholder={t.sidebarProjectName}
            className="sidebar-inline-input"
          />
          <input
            type="password"
            value={newProjectPassword}
            onChange={(e) => setNewProjectPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreateProject(); if (e.key === 'Escape') { setShowNewProject(false); setNewProjectPassword('') } }}
            placeholder={t.projPasswordPlaceholder}
            className="sidebar-inline-input"
          />
        </div>
      )}

      <div className="sidebar-tree">
        {projects?.map((project) => (
          <ProjectNode
            key={project.id}
            project={project}
            isExpanded={expandedProjects.has(project.id)}
            isActive={activeProjectId === project.id}
            activeThreadId={activeThreadId}
            expandedFolders={expandedFolders}
            onToggle={() => {
              if (project.is_locked && !unlockedProjects.has(project.id)) {
                setUnlockingProject(project.id)
                setUnlockPassword('')
                setUnlockError('')
                return
              }
              toggleProject(project.id)
            }}
            onSelect={() => {
              if (project.is_locked && !unlockedProjects.has(project.id)) {
                setUnlockingProject(project.id)
                setUnlockPassword('')
                setUnlockError('')
                return
              }
              navigateToProject(project.id)
            }}
            onSelectThread={(threadId: string) => navigateToThread(threadId, project.id)}
            onToggleFolder={toggleFolder}
            isLocked={!!project.is_locked && !unlockedProjects.has(project.id)}
          />
        ))}
      </div>

      <div className="sidebar-footer">
        {authUser && (
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{authUser.display_name}</span>
            <span className="sidebar-user-role">{authUser.role}</span>
          </div>
        )}
        <button className={`sidebar-nav-item ${activeView === 'wallet' ? 'active' : ''}`} onClick={navigateToWallet}>
          <Wallet size={16} />
          <span>{t.navWallet}</span>
        </button>
        <button className={`sidebar-nav-item ${activeView === 'marketplace' ? 'active' : ''}`} onClick={navigateToMarketplace}>
          <Store size={16} />
          <span>{t.navMarketplace}</span>
        </button>
        {authUser?.role === 'admin' && (
          <button className={`sidebar-nav-item ${activeView === 'admin' ? 'active' : ''}`} onClick={navigateToAdmin}>
            <Shield size={16} />
            <span>Admin</span>
          </button>
        )}
        <button className={`sidebar-nav-item sidebar-nav-agent ${activeView === 'agent' ? 'active' : ''}`} onClick={navigateToAgent}>
          <Bot size={16} />
          <span>Agent AI</span>
        </button>
        <button className={`sidebar-nav-item sidebar-nav-social ${activeView === 'social-agent' ? 'active' : ''}`} onClick={navigateToSocialAgent}>
          <Share2 size={16} />
          <span>Agent SocialAI</span>
        </button>
        <button className={`sidebar-nav-item sidebar-nav-seo ${activeView === 'content-seo' ? 'active' : ''}`} onClick={navigateToContentSeo}>
          <Search size={16} />
          <span>ContentSEO-AI</span>
        </button>
        <button className={`sidebar-nav-item ${activeView === 'app-builder' ? 'active' : ''}`} onClick={() => navigateToAppBuilder()}>
          <Code size={16} />
          <span>{t.navAppBuilder}</span>
        </button>
        <button className={`sidebar-nav-item ${activeView === 'files' ? 'active' : ''}`} onClick={navigateToFiles}>
          <HardDrive size={16} />
          <span>Pliki</span>
        </button>
        <button className={`sidebar-nav-item ${activeView === 'api-keys' ? 'active' : ''}`} onClick={navigateToApiKeys}>
          <Key size={16} />
          <span>{t.navApiKeys}</span>
        </button>
        <button className={`sidebar-nav-item ${activeView === 'settings' ? 'active' : ''}`} onClick={navigateToSettings}>
          <Settings size={16} />
          <span>{t.navSettings}</span>
        </button>
        <button className="sidebar-nav-item sidebar-logout" onClick={() => { api.logout().catch(() => {}); logout() }}>
          <LogOut size={16} />
          <span>{t.navSignOut}</span>
        </button>
      </div>

      {showImport && (
        <div className="import-modal-overlay" onClick={() => !importing && setShowImport(false)}>
          <div className="import-modal" onClick={e => e.stopPropagation()}>
            <div className="import-modal-header">
              <h2>Import czatów</h2>
              <button className="import-modal-close" onClick={() => !importing && setShowImport(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="import-modal-body">
              <label className="import-modal-field">
                <span className="import-modal-label">Miejsce zapisu</span>
                <select
                  value={importProjectId}
                  onChange={e => setImportProjectId(e.target.value)}
                  className="import-modal-select"
                  disabled={importing}
                >
                  <option value="__standalone_import__">Folder "Import" (poza projektami)</option>
                  {projects?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>

              <label className="import-modal-field">
                <span className="import-modal-label">Format LLM</span>
                <select
                  value={importFormat}
                  onChange={e => setImportFormat(e.target.value)}
                  className="import-modal-select"
                  disabled={importing}
                >
                  <option value="openai">OpenAI (API format)</option>
                  <option value="chatgpt">ChatGPT (conversations.json)</option>
                  <option value="anthropic">Anthropic Claude (API)</option>
                  <option value="claude">Claude (eksport z Claude.ai)</option>
                  <option value="google">Google Gemini</option>
                  <option value="synapsios">Synapsios</option>
                </select>
              </label>

              <label className="import-modal-field">
                <span className="import-modal-label">Pliki do importu</span>
                <div
                  className={`import-modal-dropzone ${importFiles.length > 0 ? 'has-files' : ''}`}
                  onClick={() => !importing && importFileRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('dragover') }}
                  onDragLeave={e => e.currentTarget.classList.remove('dragover')}
                  onDrop={e => {
                    e.preventDefault()
                    e.currentTarget.classList.remove('dragover')
                    const files = Array.from(e.dataTransfer.files).filter(f => f.name.endsWith('.json'))
                    if (files.length) setImportFiles(prev => [...prev, ...files])
                  }}
                >
                  <FileDown size={24} className="import-dropzone-icon" />
                  {importFiles.length === 0 ? (
                    <span>Kliknij lub przeciągnij pliki JSON</span>
                  ) : (
                    <span>{importFiles.length} {importFiles.length === 1 ? 'plik' : importFiles.length < 5 ? 'pliki' : 'plików'} wybrano</span>
                  )}
                </div>
                <input
                  ref={importFileRef}
                  type="file"
                  accept=".json"
                  multiple
                  style={{ display: 'none' }}
                  onChange={e => {
                    const files = Array.from(e.target.files || [])
                    if (files.length) setImportFiles(prev => [...prev, ...files])
                    if (importFileRef.current) importFileRef.current.value = ''
                  }}
                />
              </label>

              {importFiles.length > 0 && (
                <div className="import-modal-file-list">
                  {importFiles.map((f, i) => (
                    <div key={`${f.name}-${i}`} className="import-modal-file-item">
                      <span>{f.name}</span>
                      <button onClick={() => setImportFiles(prev => prev.filter((_, j) => j !== i))} disabled={importing}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {importError && <div className="import-modal-error">{importError}</div>}
              {importing && importSuccess > 0 && (
                <div className="import-modal-progress">Zaimportowano {importSuccess} z {importFiles.length}...</div>
              )}
            </div>

            <div className="import-modal-footer">
              <p className="import-modal-hint">Każdy plik zostanie zaimportowany jako osobny czat do folderu "Importowane".</p>
              <div className="import-modal-actions">
                <button className="import-modal-cancel" onClick={() => !importing && setShowImport(false)} disabled={importing}>
                  {t.cancel}
                </button>
                <button
                  className="import-modal-submit"
                  onClick={handleImport}
                  disabled={importing || importFiles.length === 0}
                >
                  {importing ? <><Loader2 size={14} className="spinner" /> Importowanie...</> : 'Importuj'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {unlockingProject && (
        <div className="sidebar-unlock-overlay">
          <div className="sidebar-unlock-form">
            <p>{t.projEnterPassword}</p>
            <input
              autoFocus
              type="password"
              value={unlockPassword}
              onChange={(e) => setUnlockPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleUnlockProject(unlockingProject); if (e.key === 'Escape') setUnlockingProject(null) }}
              className="sidebar-inline-input"
            />
            {unlockError && <span className="sidebar-unlock-error">{unlockError}</span>}
            <div className="sidebar-unlock-actions">
              <button className="button-primary" onClick={() => handleUnlockProject(unlockingProject)}>{t.projUnlock}</button>
              <button onClick={() => setUnlockingProject(null)}>{t.cancel}</button>
            </div>
          </div>
        </div>
      )}
    </aside>
  )
}

function ProjectNode({ project, isExpanded, isActive, activeThreadId, expandedFolders, onToggle, onSelect, onSelectThread, onToggleFolder, isLocked }: {
  project: Project
  isExpanded: boolean
  isActive: boolean
  activeThreadId: string | null
  expandedFolders: Set<string>
  onToggle: () => void
  onSelect: () => void
  onSelectThread: (id: string) => void
  onToggleFolder: (id: string) => void
  isLocked?: boolean
}) {
  const t = useT()
  const { data: folders } = useFolders(isExpanded ? project.id : null)
  const { data: threads } = useThreads(isExpanded ? project.id : null)
  const [showNewFolder, setShowNewFolder] = useState(false)
  const [showNewThread, setShowNewThread] = useState(false)
  const [newName, setNewName] = useState('')
  const [newThreadProvider, setNewThreadProvider] = useState('')
  const [newThreadModel, setNewThreadModel] = useState('')
  const [newThreadLanguage, setNewThreadLanguage] = useState('')
  const [dropTarget, setDropTarget] = useState(false)
  const createFolder = useCreateFolder()
  const createThread = useCreateThread()
  const moveFolder = useMoveFolder()
  const moveThread = useMoveThread()
  const { data: availableModels } = useAvailableModels()

  const rootFolders = useMemo(() => folders?.filter((f) => !f.parent_folder_id) || [], [folders])
  const rootThreads = useMemo(() => threads?.filter((t) => !t.folder_id) || [], [threads])

  const getSubFolders = (parentId: string) => folders?.filter((f) => f.parent_folder_id === parentId) || []
  const getThreadsInFolder = (folderId: string) => threads?.filter((t) => t.folder_id === folderId) || []

  const selectedProviderModels = availableModels?.find(p => p.provider_type === newThreadProvider)?.models || []

  const handleCreateFolder = () => {
    if (!newName.trim()) return
    createFolder.mutate({ project_id: project.id, name: newName.trim() }, {
      onSuccess: () => { setNewName(''); setShowNewFolder(false) },
    })
  }

  const handleCreateThread = (folderId?: string) => {
    if (!newName.trim()) return
    createThread.mutate({
      project_id: project.id,
      folder_id: folderId || null,
      title: newName.trim(),
      default_provider: newThreadProvider || undefined,
      default_model: newThreadModel || undefined,
      language: newThreadLanguage || undefined,
    }, {
      onSuccess: () => { setNewName(''); setShowNewThread(false); setNewThreadProvider(''); setNewThreadModel(''); setNewThreadLanguage('') },
    })
  }

  // Drop on project root = move to root (no folder)
  const handleDragOver = useCallback((e: DragEvent) => {
    if (e.dataTransfer.types.includes(DRAG_TYPE)) {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      setDropTarget(true)
    }
  }, [])

  const handleDragLeave = useCallback(() => setDropTarget(false), [])

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault()
    setDropTarget(false)
    try {
      const payload: DragPayload = JSON.parse(e.dataTransfer.getData(DRAG_TYPE))
      if (payload.projectId !== project.id) return
      if (payload.type === 'folder') {
        moveFolder.mutate({ id: payload.id, projectId: project.id, parent_folder_id: null })
      } else if (payload.type === 'thread') {
        moveThread.mutate({ id: payload.id, projectId: project.id, folder_id: null })
      }
    } catch {}
  }, [project.id, moveFolder, moveThread])

  return (
    <div className="tree-project">
      <div
        className={`tree-item tree-project-item ${isActive ? 'active' : ''} ${dropTarget ? 'tree-drop-target' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <button className="tree-toggle" onClick={onToggle}>
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <div className="tree-color-dot" style={{ backgroundColor: project.icon_color }} />
        <button className="tree-label" onClick={() => { onSelect(); if (!isExpanded) onToggle() }}>
          {project.name}
        </button>
        {isLocked && <Lock size={12} className="tree-lock-icon" />}
        {isExpanded && (
          <div className="tree-actions">
            <button className="tree-action-btn" onClick={(e) => { e.stopPropagation(); setShowNewFolder(true); setShowNewThread(false); setNewName('') }} title={t.sidebarNewFolder}>
              <FolderPlus size={12} />
            </button>
            <button className="tree-action-btn" onClick={(e) => { e.stopPropagation(); setShowNewThread(true); setShowNewFolder(false); setNewName('') }} title={t.sidebarNewThread}>
              <MessageSquarePlus size={12} />
            </button>
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="tree-children">
          {showNewFolder && (
            <div className="sidebar-inline-form" style={{ paddingLeft: 24 }}>
              <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') setShowNewFolder(false) }}
                placeholder={t.sidebarFolderName} className="sidebar-inline-input" />
            </div>
          )}
          {showNewThread && (
            <div className="sidebar-inline-form sidebar-thread-form" style={{ paddingLeft: 24 }}>
              <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateThread(); if (e.key === 'Escape') { setShowNewThread(false); setNewThreadProvider(''); setNewThreadModel(''); setNewThreadLanguage('') } }}
                placeholder={t.sidebarThreadTitle} className="sidebar-inline-input" />
              {availableModels && availableModels.length > 0 && (
                <div className="sidebar-model-selectors">
                  <select
                    className="sidebar-inline-select"
                    value={newThreadProvider}
                    onChange={(e) => {
                      setNewThreadProvider(e.target.value)
                      const models = availableModels.find(p => p.provider_type === e.target.value)?.models || []
                      setNewThreadModel(models[0] || '')
                    }}
                  >
                    <option value="">{t.threadSelectProvider}</option>
                    {availableModels.map(p => (
                      <option key={p.provider_type} value={p.provider_type}>{p.display_name}</option>
                    ))}
                  </select>
                  {newThreadProvider && selectedProviderModels.length > 0 && (
                    <select
                      className="sidebar-inline-select"
                      value={newThreadModel}
                      onChange={(e) => setNewThreadModel(e.target.value)}
                    >
                      {selectedProviderModels.map(m => (
                        <option key={m} value={m}>{getModelDisplayName(m)}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}
              <select
                className="sidebar-inline-select"
                value={newThreadLanguage}
                onChange={(e) => setNewThreadLanguage(e.target.value)}
              >
                <option value="">{t.threadSelectLanguage}</option>
                <option value="English">English</option>
                <option value="Polish">Polski</option>
                <option value="Slovak">Slovensky</option>
                <option value="German">Deutsch</option>
                <option value="French">Français</option>
                <option value="Spanish">Español</option>
                <option value="Italian">Italiano</option>
                <option value="Portuguese">Português</option>
                <option value="Dutch">Nederlands</option>
                <option value="Russian">Русский</option>
                <option value="Ukrainian">Українська</option>
                <option value="Czech">Česky</option>
                <option value="Japanese">日本語</option>
                <option value="Chinese">中文</option>
                <option value="Korean">한국어</option>
                <option value="Arabic">العربية</option>
                <option value="Hindi">हिन्दी</option>
                <option value="Turkish">Türkçe</option>
                <option value="Swedish">Svenska</option>
                <option value="Norwegian">Norsk</option>
              </select>
            </div>
          )}

          {rootFolders.map((folder) => (
            <FolderNode
              key={folder.id}
              folder={folder}
              isExpanded={expandedFolders.has(folder.id)}
              activeThreadId={activeThreadId}
              expandedFolders={expandedFolders}
              subFolders={getSubFolders(folder.id)}
              threads={getThreadsInFolder(folder.id)}
              allFolders={folders || []}
              allThreads={threads || []}
              onToggle={() => onToggleFolder(folder.id)}
              onSelectThread={onSelectThread}
              onToggleFolder={onToggleFolder}
              projectId={project.id}
              moveFolder={moveFolder}
              moveThread={moveThread}
            />
          ))}

          {rootThreads.map((thread) => (
            <ThreadNode key={thread.id} thread={thread} isActive={activeThreadId === thread.id} onSelect={() => onSelectThread(thread.id)} projectId={project.id} />
          ))}
        </div>
      )}
    </div>
  )
}

function FolderNode({ folder, isExpanded, activeThreadId, expandedFolders, subFolders, threads, allFolders, allThreads, onToggle, onSelectThread, onToggleFolder, projectId, moveFolder, moveThread }: {
  folder: FolderType
  isExpanded: boolean
  activeThreadId: string | null
  expandedFolders: Set<string>
  subFolders: FolderType[]
  threads: Thread[]
  allFolders: FolderType[]
  allThreads: Thread[]
  onToggle: () => void
  onSelectThread: (id: string) => void
  onToggleFolder: (id: string) => void
  projectId: string
  moveFolder: ReturnType<typeof useMoveFolder>
  moveThread: ReturnType<typeof useMoveThread>
}) {
  const [dropTarget, setDropTarget] = useState(false)
  const hasChildren = subFolders.length > 0 || threads.length > 0

  // Drag this folder
  const handleDragStart = useCallback((e: DragEvent) => {
    const payload: DragPayload = { type: 'folder', id: folder.id, projectId, parentFolderId: folder.parent_folder_id }
    e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(payload))
    e.dataTransfer.effectAllowed = 'move'
  }, [folder.id, projectId, folder.parent_folder_id])

  // Accept drops on this folder
  const handleDragOver = useCallback((e: DragEvent) => {
    if (e.dataTransfer.types.includes(DRAG_TYPE)) {
      e.preventDefault()
      e.stopPropagation()
      e.dataTransfer.dropEffect = 'move'
      setDropTarget(true)
    }
  }, [])

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.stopPropagation()
    setDropTarget(false)
  }, [])

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDropTarget(false)
    try {
      const payload: DragPayload = JSON.parse(e.dataTransfer.getData(DRAG_TYPE))
      if (payload.projectId !== projectId) return
      // Don't drop folder into itself
      if (payload.type === 'folder' && payload.id === folder.id) return
      // Don't drop folder into its own child (prevent circular)
      if (payload.type === 'folder' && isDescendant(payload.id, folder.id, allFolders)) return

      if (payload.type === 'folder') {
        moveFolder.mutate({ id: payload.id, projectId, parent_folder_id: folder.id })
      } else if (payload.type === 'thread') {
        moveThread.mutate({ id: payload.id, projectId, folder_id: folder.id })
      }
      // Auto-expand target folder
      if (!isExpanded) onToggle()
    } catch {}
  }, [folder.id, projectId, moveFolder, moveThread, allFolders, isExpanded, onToggle])

  return (
    <div className="tree-folder">
      <div
        className={`tree-item tree-folder-item ${dropTarget ? 'tree-drop-target' : ''}`}
        draggable
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <button className="tree-toggle" onClick={onToggle}>
          {hasChildren ? (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span style={{ width: 14 }} />}
        </button>
        <Folder size={14} className="tree-icon folder-icon" />
        <button className="tree-label" onClick={onToggle}>{folder.name}</button>
      </div>

      {isExpanded && (
        <div className="tree-children">
          {subFolders.map((sf) => (
            <FolderNode
              key={sf.id}
              folder={sf}
              isExpanded={expandedFolders.has(sf.id)}
              activeThreadId={activeThreadId}
              expandedFolders={expandedFolders}
              subFolders={allFolders.filter((f) => f.parent_folder_id === sf.id)}
              threads={allThreads.filter((t) => t.folder_id === sf.id)}
              allFolders={allFolders}
              allThreads={allThreads}
              onToggle={() => onToggleFolder(sf.id)}
              onSelectThread={onSelectThread}
              onToggleFolder={onToggleFolder}
              projectId={projectId}
              moveFolder={moveFolder}
              moveThread={moveThread}
            />
          ))}
          {threads.map((thread) => (
            <ThreadNode key={thread.id} thread={thread} isActive={activeThreadId === thread.id} onSelect={() => onSelectThread(thread.id)} projectId={projectId} />
          ))}
        </div>
      )}
    </div>
  )
}

function ThreadNode({ thread, isActive, onSelect, projectId }: { thread: Thread; isActive: boolean; onSelect: () => void; projectId: string }) {
  const handleDragStart = useCallback((e: DragEvent) => {
    const payload: DragPayload = { type: 'thread', id: thread.id, projectId, parentFolderId: thread.folder_id }
    e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(payload))
    e.dataTransfer.effectAllowed = 'move'
  }, [thread.id, projectId, thread.folder_id])

  return (
    <div
      className={`tree-item tree-thread-item ${isActive ? 'active' : ''}`}
      onClick={onSelect}
      draggable
      onDragStart={handleDragStart}
    >
      <MessageSquare size={14} className="tree-icon thread-icon" />
      <span className="tree-label">{thread.title}</span>
      {thread.pinned === 1 && <Pin size={10} className="pin-icon" />}
    </div>
  )
}

// Check if `targetId` is a descendant of `folderId` (to prevent circular drops)
function isDescendant(folderId: string, targetId: string, allFolders: FolderType[]): boolean {
  let current = allFolders.find(f => f.id === targetId)
  while (current) {
    if (current.parent_folder_id === folderId) return true
    current = allFolders.find(f => f.id === current!.parent_folder_id)
  }
  return false
}
